import { Component, OnInit, Inject } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormGroup, Validators, FormBuilder, FormArray } from "@angular/forms";
import { ScheduledTask } from "../../../enums/escheduled-task.enum";
import { MedicalOrderScannType } from "../../../model/medical-order-scann-type";
import { FilterService } from "../../../services/filter.service";
import { BillingCloseService } from "../../../services/billing/billing-close.service";
import { NotificationToastService } from "../../../services/notification-toast.service";
import { BillingClosePreBilling } from "../../../model/billing/billing-close-pre-billing";
import { BillingClosePeriod } from "../../../model/billing/billing-close-period";
import { ScheduleTaskService } from "../../../services/eschedule-task/eschedule-task.service";
import {
  BillingScheduleTaskParameter,
  BillingClosePreBillingParameter,
} from "../../../model/schedule-task/billing-schedule-task-paramater";
import { ConfirmDialogService } from "../../../services/confirm-dialog.service";
import { AuthService } from "../../../services/auth.service";

export interface BillingCloseTaskDialogRequest {
  escheduledTaskId: number;
  taskName: string;
  invoiceNumber: string;
  invoicePoint: string;
  invoiceLabelType: string;
  agruparPorPlan: boolean;
  agruparPorPlanYContraste: boolean;
  medicalAppointmentIds: number[];
  medicalAppointmentProcedureIds: number[];
}

export class BillingCloseTaskDialogResponse {
  constructor(public reload: boolean) {}
}

@Component({
  selector: "app-billing-close-task-dialog",
  templateUrl: "./billing-close-task-dialog.component.html",
  styleUrls: ["./billing-close-task-dialog.component.css"],
})
export class BillingCloseTaskDialogComponent implements OnInit {
  loading: boolean;
  loadingAction: boolean;
  formExportImages: FormGroup;
  formPreBilling: FormGroup;
  formTask: FormGroup;
  minutes: number[] = [];
  hours: number[] = [];
  medicalOrderScannTypes: MedicalOrderScannType[] = [];
  billingClosePreBillings: BillingClosePreBilling[] = [];

  constructor(
    public dialogRef: MatDialogRef<BillingCloseTaskDialogComponent>,
    private formBuilder: FormBuilder,
    private notificationToastService: NotificationToastService,
    private billingCloseService: BillingCloseService,
    private escheduleTaskService: ScheduleTaskService,
    private confirmDialogService: ConfirmDialogService,
    private filterService: FilterService,
    private authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public data: BillingCloseTaskDialogRequest
  ) {
    this.formTask = this.formBuilder.group({
      name: [data.taskName, Validators.required],
      date: [new Date(), Validators.required],
      hour: [new Date().getHours(), Validators.required],
      minute: [new Date().getMinutes(), Validators.required],
    });

    this.formExportImages = this.formBuilder.group({
      MedicalOrderScannTypes: [[], Validators.required],
    });

    this.formPreBilling = this.formBuilder.group({
      BillingClosePreBillings: this.formBuilder.array([]),
    });
  }

  get billingClosePreBillingsArrayGroup() {
    return <FormArray>this.formPreBilling.get("BillingClosePreBillings");
  }
  get configurationVisible(): boolean {
    return this.isBillingExportImages || this.isBillingPreBilling;
  }
  get configuracionCompleted(): boolean {
    return this.isBillingExportImages
      ? this.formExportImages.valid
      : this.billingClosePreBillingsArrayGroup.length > 0;
  }
  get taskCompleted(): boolean {
    return this.formTask ? this.formTask.valid : false;
  }
  get isBillingValidationOnLine(): boolean {
    return this.data.escheduledTaskId === ScheduledTask.billingValidationOnLine;
  }
  get isBillingExportImages(): boolean {
    return this.data.escheduledTaskId === ScheduledTask.billingExportImages;
  }
  get isBillingExportStudyReport(): boolean {
    return (
      this.data.escheduledTaskId === ScheduledTask.billingExportStudyReport
    );
  }
  get isBillingPreBilling(): boolean {
    return this.data.escheduledTaskId === ScheduledTask.billingPreBilling;
  }
  get isExportAttached(): boolean {
    return this.data.escheduledTaskId === ScheduledTask.exportAttached;
  }
  get fExportImages() {
    return this.formExportImages.controls;
  }
  get f() {
    return this.formTask.controls;
  }

  getBillingCloseDate(): Date | null {
    return this.authService.getBillingCloseDate();
  }

  async ngOnInit() {
    await this.loadStepConfiguration();
    this.loadStepTask();
  }

  async loadStepConfiguration() {
    if (this.isBillingExportImages) {
      await this.loadMedicalOrderScannTypesFilter();
    } else if (this.isBillingPreBilling) {
      await this.loadBillingClosePreBilling();
    }
  }

  async loadMedicalOrderScannTypesFilter() {
    try {
      this.loading = true;
      this.medicalOrderScannTypes =
        await this.filterService.getMedicalOrderScannTypesFilter();
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Tarea programada"
      );
    } finally {
      this.loading = false;
    }
  }

  async loadBillingClosePreBilling() {
    try {
      if (this.billingClosePreBillingsArrayGroup) {
        this.loading = true;
        this.billingClosePreBillings =
          await this.billingCloseService.getBillingClosePreBillings(
            this.data.medicalAppointmentProcedureIds
          );
        this.billingClosePreBillings.forEach((item) => {
          const billingCloseDate = this.getBillingCloseDate();


          const now = billingCloseDate ? billingCloseDate : new Date();
          now.setHours(0, 0, 0, 0);


          const actualPeriod = item.BillingClosePeriods.find((b) => {
            const initDate = new Date(b.Init);
            initDate.setHours(0, 0, 0, 0);
            const endDate = new Date(b.End);
            endDate.setHours(0, 0, 0, 0);
            return initDate <= now && endDate >= now;
          });


          const row = this.formBuilder.group({
            BillingGroupName: [
              { value: item.BillingGroupName, disabled: true },
              Validators.required,
            ],
            BillingMedicalAppointmentProcedures: [
              item.BillingMedicalAppointmentProcedures,
            ],
            SelectedBillingClosePeriod: [
              actualPeriod ? actualPeriod : item.BillingClosePeriods[0],
              Validators.required,
            ],
          });
          this.billingClosePreBillingsArrayGroup.push(row);
        });
      }
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Tarea programada"
      );
    } finally {
      this.loading = false;
    }
  }

  getBillingClosePeriod(index: number): BillingClosePeriod[] {
    return this.billingClosePreBillings[index].BillingClosePeriods;
  }

  private loadStepTask() {
    for (let index = 0; index < 24; index++) {
      this.hours.push(index);
    }
    for (let index = 0; index < 60; index++) {
      this.minutes.push(index);
    }
  }

  cancel() {
    const response = new BillingCloseTaskDialogResponse(false);
    this.dialogRef.close(response);
  }

  async finish() {
    if (this.formTask.valid) {
      try {
        if (!this.isPastTask()) {
          this.scheduleTaskCreate();
        } else {
          this.confirmDialogService
            .confirm(
              "Tarea programada",
              "La tarea se ejecutará en este momento. Desa continuar?",
              "Sí",
              "No",
              false
            )
            .subscribe((response) => {
              if (response) {
                this.scheduleTaskCreate();
              }
            });
        }
      } catch (err) {
        this.notificationToastService.error(
          JSON.stringify(err.error),
          "Tarea programada"
        );
      } finally {
        this.loadingAction = false;
      }
    }
  }

  isPastTask() {
    const now = new Date();
    const date = <Date>this.formTask.getRawValue().date;
    const hour = this.formTask.getRawValue().hour;
    const minute = this.formTask.getRawValue().minute;
    date.setMinutes(minute);
    date.setHours(hour);
    date.setSeconds(0);
    now.setSeconds(0);
    return date <= now;
  }

  async scheduleTaskCreate() {
    this.loadingAction = true;
    const parameters = new BillingScheduleTaskParameter();
    parameters.BillingClosePreBillings = [];
    const billingClosePreBillings = <BillingClosePreBilling[]>(
      this.formPreBilling.getRawValue().BillingClosePreBillings
    );
    if (billingClosePreBillings) {
      billingClosePreBillings.forEach((item) => {
        parameters.BillingClosePreBillings.push({
          SelectedBillingClosePeriod: item.SelectedBillingClosePeriod,
          MedicalAppointmentProcedureIds:
            item.BillingMedicalAppointmentProcedures.map(
              (b) => b.MedicalAppointmentProcedureId
            ),
        });
      });
    }

    parameters.MedicalOrderScannTypes = <MedicalOrderScannType[]>(
      this.formExportImages.getRawValue().MedicalOrderScannTypes
    );
    parameters.MedicalAppointmentProcedureIds =
      this.data.medicalAppointmentProcedureIds;
    parameters.InvoiceNumber = this.data.invoiceNumber;
    parameters.InvoicePoint = this.data.invoicePoint;
    parameters.InvoiceLabelType = this.data.invoiceLabelType;
    parameters.MedicalAppointmentIds = this.data.medicalAppointmentIds;
    parameters.AgruparPorPlan = this.data.agruparPorPlan;
    parameters.AgruparPorPlanYContraste = this.data.agruparPorPlanYContraste;

    const name = this.formTask.getRawValue().name;
    const date = this.formTask.getRawValue().date;
    const hour = this.formTask.getRawValue().hour;
    const minute = this.formTask.getRawValue().minute;

    await this.escheduleTaskService.scheduleTaskCreate(
      this.data.escheduledTaskId,
      date,
      hour,
      minute,
      name,
      JSON.stringify(parameters)
    );
    const response = new BillingCloseTaskDialogResponse(false);
    this.dialogRef.close(response);
  }

  async preBilling() {
    try {
      this.loadingAction = true;
      const billingClosePreBillings = <BillingClosePreBilling[]>(
        this.formPreBilling.getRawValue().BillingClosePreBillings
      );
      await this.billingCloseService.billingOrderControlPreBill(
        billingClosePreBillings
      );
      const response = new BillingCloseTaskDialogResponse(true);
      this.dialogRef.close(response);
    } catch (err) {
      this.notificationToastService.error(
        JSON.stringify(err.error),
        "Tarea programada"
      );
    } finally {
      this.loadingAction = false;
    }
  }
}
